import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CheckCircle2, Loader2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription } from 'components/ui/alert';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import LoadingState from 'components/shared/LoadingState';
import { cn } from 'lib/utils';
import { getBranding, uploadBrandingAsset } from '../../services/api';

const ASSET_SLOTS = [
  { key: 'favicon', label: 'Favicon', size: '32x32', accept: '.ico,.png' },
  { key: 'apple_touch_icon', label: 'Apple Touch Icon', size: '180x180', accept: '.png' },
  { key: 'pwa_icon_192', label: 'PWA Icon (Small)', size: '192x192', accept: '.png' },
  { key: 'pwa_icon_512', label: 'PWA Icon (Large)', size: '512x512', accept: '.png' },
  { key: 'header_logo', label: 'Header Logo', size: '~126x32', accept: '.png,.svg,.webp' },
  { key: 'footer_logo', label: 'Footer Logo', size: '~126x32', accept: '.png,.svg,.webp' },
  { key: 'og_image_default', label: 'Default Social Image', size: '1200x630', accept: '.png,.jpg,.jpeg,.webp' },
  { key: 'org_logo', label: 'Organization Logo', size: '512x512', accept: '.png' },
];

export default function BrandingManager() {
  const [assets, setAssets] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [uploading, setUploading] = useState({});
  // Per-slot feedback so concurrent uploads never clobber each other:
  // key -> { type: 'success' | 'error', message }
  const [slotStatus, setSlotStatus] = useState({});
  const fileInputRefs = useRef({});

  const fetchBranding = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await getBranding();
      setAssets(data.assets || {});
    } catch (err) {
      setLoadError('Failed to load branding assets');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBranding();
  }, [fetchBranding]);

  const handleUpload = async (assetKey, file) => {
    const label = ASSET_SLOTS.find((s) => s.key === assetKey)?.label || assetKey;

    setUploading((prev) => ({ ...prev, [assetKey]: true }));
    setSlotStatus((prev) => ({ ...prev, [assetKey]: null }));

    try {
      const data = await uploadBrandingAsset(assetKey, file);
      setAssets((prev) => ({ ...prev, [data.asset_key]: data.asset_url }));
      setSlotStatus((prev) => ({
        ...prev,
        [assetKey]: { type: 'success', message: 'Uploaded successfully' },
      }));
      toast.success(`${label} uploaded successfully`);
    } catch (err) {
      const message = err.response?.data?.detail || 'Upload failed';
      setSlotStatus((prev) => ({ ...prev, [assetKey]: { type: 'error', message } }));
      toast.error(`${label}: ${message}`);
      console.error(err);
    } finally {
      setUploading((prev) => ({ ...prev, [assetKey]: false }));
    }
  };

  const handleFileSelect = (assetKey) => (event) => {
    const file = event.target.files?.[0];
    if (file) {
      handleUpload(assetKey, file);
    }
    // Reset input so same file can be re-selected
    event.target.value = '';
  };

  const header = (
    <>
      <h2 className="text-token-xl font-semibold">Branding</h2>
      <p className="mb-6 text-sm text-muted-foreground">
        Manage logos, favicons, and social images used across the storefront.
      </p>
    </>
  );

  if (loading) {
    return (
      <div>
        {header}
        <LoadingState message="Loading branding assets..." />
      </div>
    );
  }

  if (loadError) {
    return (
      <div>
        {header}
        <Alert variant="destructive" className="flex items-center justify-between gap-4">
          <AlertDescription>{loadError}</AlertDescription>
          <Button variant="outline" size="sm" onClick={fetchBranding}>
            Retry
          </Button>
        </Alert>
      </div>
    );
  }

  return (
    <div>
      {header}

      <div className="grid grid-cols-[repeat(auto-fill,minmax(240px,1fr))] gap-4">
        {ASSET_SLOTS.map((slot) => {
          const currentUrl = assets[slot.key];
          const isUploading = !!uploading[slot.key];
          const status = slotStatus[slot.key];

          return (
            <div
              key={slot.key}
              className="flex flex-col rounded-token-large border border-borderGrey bg-white p-4"
            >
              <div className="mb-1 flex items-center gap-1.5">
                <span className="truncate text-token-md font-semibold">{slot.label}</span>
                {currentUrl && (
                  <CheckCircle2 size={16} className="shrink-0 text-success" aria-label="Asset set" />
                )}
              </div>
              <Badge variant="outline" className="mb-3 self-start">
                {slot.size}
              </Badge>

              <div className="flex h-[120px] items-center justify-center overflow-hidden rounded-token-md bg-neutral-100">
                {isUploading ? (
                  <Loader2 className="h-8 w-8 animate-spin text-primary" aria-hidden="true" />
                ) : currentUrl ? (
                  <img
                    src={currentUrl}
                    alt={slot.label}
                    className="max-h-full max-w-full object-contain"
                  />
                ) : (
                  <span className="text-token-base text-muted-foreground">Not set</span>
                )}
              </div>

              {/* Per-slot inline feedback; fixed min-height so cards don't jump */}
              <p
                className={cn(
                  'mt-2 min-h-[18px] text-token-base',
                  status?.type === 'error' ? 'text-destructive' : 'text-success'
                )}
              >
                {status?.message}
              </p>

              <div className="mt-auto pt-2">
                <input
                  type="file"
                  accept={slot.accept}
                  hidden
                  ref={(el) => (fileInputRefs.current[slot.key] = el)}
                  onChange={handleFileSelect(slot.key)}
                />
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => fileInputRefs.current[slot.key]?.click()}
                  disabled={isUploading}
                >
                  {isUploading ? (
                    <Loader2 className="animate-spin" aria-hidden="true" />
                  ) : (
                    <Upload aria-hidden="true" />
                  )}
                  {currentUrl ? 'Replace' : 'Upload'}
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
